import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, useSearchParams } from "react-router-dom";
import {
  getClientViewListingSourceRun,
  listClientViewSettingsDataSources,
  runClientViewListingSource,
  runClientViewVendorSearchSource,
  updateClientViewListingSource,
  updateClientViewVendorSearchSource,
} from "../../api/clientSettingsApi";
import {
  getVendorDirectoryCollectionRun,
  listVendorDirectorySourceRecords,
} from "../../api/clientVendorDirectoryCollectionApi";
import VendorDirectoryPermissionGate from "../../components/casamap/admin/VendorDirectoryPermissionGate";
import { VENDOR_DIRECTORY_PERMISSIONS } from "../../auth/casamapaVendorDirectoryPermissions";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import ManageVendorSearchSourceModal from "../../components/clientPortal/ManageVendorSearchSourceModal";
import { formatClientDateTime, formatStatusLabel } from "../../utils/clientPortalFormat";
import { filterVendorSearchesForViewer } from "../../utils/vendorSearchSourceVisibility";
import "../../styles/casamapaVendorDirectoryAdmin.css";

function healthBadgeClass(status) {
  if (status === "healthy") return "cvd-badge--published";
  if (status === "blocked" || status === "failing") return "cvd-badge--suspended";
  if (status === "degraded") return "cvd-badge--warning";
  return "cvd-badge--draft";
}

function runBadgeClass(status) {
  if (status === "completed") return "cvd-badge--published";
  if (["failed", "cancelled", "blocked"].includes(status)) return "cvd-badge--suspended";
  if (["running", "completed_with_errors", "rate_limited", "cancel_requested"].includes(status)) {
    return "cvd-badge--warning";
  }
  return "cvd-badge--draft";
}

function formatVendorLastResults(source) {
  const summary = source.lastRunSummary;
  if (!summary) return source.lastRunStatus ? formatStatusLabel(source.lastRunStatus) : "—";
  const parts = [];
  if (summary.recordsStaged != null) parts.push(`${summary.recordsStaged} staged`);
  if (summary.recordsRejected) parts.push(`${summary.recordsRejected} rejected`);
  if (summary.recordsSkipped) parts.push(`${summary.recordsSkipped} skipped`);
  if (summary.errorSummary) parts.push("errors");
  return parts.length ? parts.join(" · ") : formatStatusLabel(source.lastRunStatus);
}

function SourceName({ name, code, description }) {
  const [tip, setTip] = useState(null);

  const showTip = (event) => {
    if (!description) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const width = 320;
    const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));
    const top = window.innerHeight - rect.bottom < 120 ? rect.top - 96 : rect.bottom + 8;
    setTip({ top: Math.max(8, top), left });
  };

  return (
    <>
      <span
        className="cp-settings-source-name"
        tabIndex={description ? 0 : undefined}
        onMouseEnter={showTip}
        onMouseLeave={() => setTip(null)}
        onFocus={showTip}
        onBlur={() => setTip(null)}
      >
        <strong>{name}</strong>
        <div className="cvd-table__meta">{code}</div>
      </span>
      {tip && description
        ? createPortal(
            <div className="cp-settings-source-tip" role="tooltip" style={{ top: tip.top, left: tip.left }}>
              {description}
            </div>,
            document.body
          )
        : null}
    </>
  );
}

function SourceToggle({ checked, disabled, onChange, label }) {
  return (
    <label className="cp-settings-toggle">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="cp-settings-toggle__track" aria-hidden />
      <span className="visually-hidden">{label}</span>
    </label>
  );
}

function ListingRunLogsPanel({ clientViewId, sourceKey, runId, onClose }) {
  const [run, setRun] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logCounts, setLogCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getClientViewListingSourceRun(clientViewId, sourceKey, runId);
      setRun(body.run || null);
      setLogs(body.logs || []);
      setLogCounts(body.logCounts || null);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load run logs.");
    } finally {
      setLoading(false);
    }
  }, [clientViewId, sourceKey, runId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section className="panel-card cp-settings-run-panel">
      <div className="cp-settings-run-panel__header">
        <div>
          <h3>Listing run logs</h3>
          <p className="muted">
            {sourceKey} · {runId ? `${String(runId).slice(0, 8)}…` : ""}
          </p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Close
        </button>
      </div>

      {loading ? <ClientPortalLoading message="Loading run details…" /> : null}
      {!loading && errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}
      {!loading && !errorMessage && run ? (
        <>
          <div className="cp-settings-run-panel__summary">
            <span className={`cvd-badge ${runBadgeClass(run.status)}`}>
              {formatStatusLabel(run.status)}
            </span>
            <span className="muted">
              Started {formatClientDateTime(run.startedAt)}
              {run.finishedAt ? ` · Completed ${formatClientDateTime(run.finishedAt)}` : ""}
            </span>
          </div>
          {run.errorMessage ? (
            <div className="cvd-alert cvd-alert--warning" role="alert">
              {run.errorMessage}
            </div>
          ) : null}
          <dl className="cp-settings-run-panel__metrics">
            <div>
              <dt>Found</dt>
              <dd>{run.recordsFound ?? 0}</dd>
            </div>
            <div>
              <dt>Created</dt>
              <dd>{run.recordsCreated ?? 0}</dd>
            </div>
            <div>
              <dt>Updated</dt>
              <dd>{run.recordsUpdated ?? 0}</dd>
            </div>
            <div>
              <dt>Failed</dt>
              <dd>{run.recordsFailed ?? 0}</dd>
            </div>
          </dl>
          {logCounts ? (
            <p className="muted">
              {logCounts.total ?? 0} log events ({logCounts.errors ?? 0} errors, {logCounts.warnings ?? 0}{" "}
              warnings)
            </p>
          ) : null}
          <h4>Step logs</h4>
          {logs.length === 0 ? (
            <p className="muted">No step logs recorded for this run yet.</p>
          ) : (
            <div className="cvd-table-wrap">
              <table className="cvd-table cvd-table--compact">
                <thead>
                  <tr>
                    <th>Step</th>
                    <th>Level</th>
                    <th>Message</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((entry) => (
                    <tr key={entry.id}>
                      <td>{entry.step || "—"}</td>
                      <td>{formatStatusLabel(entry.level)}</td>
                      <td>{entry.message}</td>
                      <td>{entry.createdAt ? formatClientDateTime(entry.createdAt) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}

function SourceRunLogsPanel({ clientViewId, runId, onClose }) {
  const [run, setRun] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const [runBody, recordsBody] = await Promise.all([
        getVendorDirectoryCollectionRun(clientViewId, runId),
        listVendorDirectorySourceRecords(clientViewId, { runId, pageSize: 50 }),
      ]);
      setRun(runBody.run || runBody);
      setRecords(recordsBody.records || []);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load run logs.");
    } finally {
      setLoading(false);
    }
  }, [clientViewId, runId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section className="panel-card cp-settings-run-panel">
      <div className="cp-settings-run-panel__header">
        <div>
          <h3>Run logs</h3>
          <p className="muted">
            {run?.sourceName || "Vendor search source"} · {runId ? `${String(runId).slice(0, 8)}…` : ""}
          </p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Close
        </button>
      </div>

      {loading ? <ClientPortalLoading message="Loading run details…" /> : null}
      {!loading && errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}
      {!loading && !errorMessage && run ? (
        <>
          <div className="cp-settings-run-panel__summary">
            <span className={`cvd-badge ${runBadgeClass(run.status)}`}>
              {formatStatusLabel(run.status)}
            </span>
            <span className="muted">
              Started {formatClientDateTime(run.startedAt || run.createdAt)}
              {run.completedAt ? ` · Completed ${formatClientDateTime(run.completedAt)}` : ""}
            </span>
          </div>
          {run.errorSummary ? (
            <div className="cvd-alert cvd-alert--warning" role="alert">
              {run.errorSummary}
            </div>
          ) : null}
          <dl className="cp-settings-run-panel__metrics">
            <div>
              <dt>Requests</dt>
              <dd>
                {run.requestsSucceeded ?? 0}/{run.requestsAttempted ?? 0}
              </dd>
            </div>
            <div>
              <dt>Received</dt>
              <dd>{run.recordsReceived ?? 0}</dd>
            </div>
            <div>
              <dt>Staged</dt>
              <dd>{run.recordsStaged ?? 0}</dd>
            </div>
            <div>
              <dt>Rejected</dt>
              <dd>{run.recordsRejected ?? 0}</dd>
            </div>
          </dl>
          <h4>Record outcomes</h4>
          {records.length === 0 ? (
            <p className="muted">No per-record outcomes for this run.</p>
          ) : (
            <div className="cvd-table-wrap">
              <table className="cvd-table cvd-table--compact">
                <thead>
                  <tr>
                    <th>Record</th>
                    <th>Outcome</th>
                    <th>Observed</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((record) => (
                    <tr key={record.id}>
                      <td>{record.rawName || record.sourceRecordKey || record.id}</td>
                      <td>{formatStatusLabel(record.outcome)}</td>
                      <td>{record.observedAt ? formatClientDateTime(record.observedAt) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}

export default function ClientSettingsDataSourcesPage() {
  const { clientViewId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedRunId = searchParams.get("run");
  const selectedListingRunId = searchParams.get("listingRun");
  const selectedListingSourceKey = searchParams.get("listingSource");

  const [vendorSearches, setVendorSearches] = useState([]);
  const [listingSources, setListingSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [bannerMessage, setBannerMessage] = useState("");
  const [pendingVendorId, setPendingVendorId] = useState("");
  const [pendingListingKey, setPendingListingKey] = useState("");
  const [actionSourceId, setActionSourceId] = useState("");
  const [actionListingKey, setActionListingKey] = useState("");
  const [managingSource, setManagingSource] = useState(null);

  const showBanner = (message) => {
    setBannerMessage(message);
    window.setTimeout(() => setBannerMessage(""), 5000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const sourcesBody = await listClientViewSettingsDataSources(clientViewId);
      setVendorSearches(filterVendorSearchesForViewer(sourcesBody.vendorSearches || []));
      setListingSources(sourcesBody.listingSources || []);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load data sources.");
    } finally {
      setLoading(false);
    }
  }, [clientViewId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleVendorToggle = async (sourceId, enabled) => {
    setPendingVendorId(sourceId);
    try {
      const body = await updateClientViewVendorSearchSource(clientViewId, sourceId, enabled);
      setVendorSearches((current) =>
        current.map((source) => (source.id === sourceId ? body.source || source : source))
      );
      showBanner(enabled ? "Vendor search source enabled." : "Vendor search source disabled.");
    } catch (error) {
      showBanner(error.message || "Unable to update vendor search source.");
      await load();
    } finally {
      setPendingVendorId("");
    }
  };

  const handleListingToggle = async (sourceKey, enabled) => {
    setPendingListingKey(sourceKey);
    try {
      const body = await updateClientViewListingSource(clientViewId, sourceKey, enabled);
      setListingSources((current) =>
        current.map((source) =>
          source.sourceKey === sourceKey ? body.source || source : source
        )
      );
      showBanner(enabled ? "Listing source enabled." : "Listing source disabled.");
    } catch (error) {
      showBanner(error.message || "Unable to update listing source.");
      await load();
    } finally {
      setPendingListingKey("");
    }
  };

  const handleListingRunNow = async (sourceKey) => {
    setActionListingKey(sourceKey);
    try {
      const body = await runClientViewListingSource(clientViewId, sourceKey);
      showBanner(body.message || "Listing ingestion run queued.");
      if (body.source) {
        setListingSources((current) =>
          current.map((source) => (source.sourceKey === sourceKey ? body.source : source))
        );
      } else {
        await load();
      }
      if (body.runId) {
        setSearchParams({ listingRun: body.runId, listingSource: sourceKey }, { replace: true });
      }
    } catch (error) {
      showBanner(error.message || "Unable to start listing ingestion run.");
    } finally {
      setActionListingKey("");
    }
  };

  const handleRunNow = async (sourceId) => {
    setActionSourceId(sourceId);
    try {
      const body = await runClientViewVendorSearchSource(clientViewId, sourceId);
      const run = body.run || body;
      showBanner(body.message || "Collection run queued.");
      if (body.source) {
        setVendorSearches((current) =>
          current.map((source) => (source.id === sourceId ? body.source : source))
        );
      } else {
        await load();
      }
      if (run?.id) {
        setSearchParams({ run: run.id }, { replace: true });
      }
    } catch (error) {
      showBanner(error.message || "Unable to start collection run.");
    } finally {
      setActionSourceId("");
    }
  };

  const openRunLogs = (runId) => {
    if (!runId) return;
    setSearchParams({ run: runId }, { replace: true });
  };

  const openListingRunLogs = (sourceKey, runId) => {
    if (!runId) return;
    setSearchParams({ listingRun: runId, listingSource: sourceKey }, { replace: true });
  };

  const closeRunLogs = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("run");
    setSearchParams(next, { replace: true });
  };

  const closeListingRunLogs = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("listingRun");
    next.delete("listingSource");
    setSearchParams(next, { replace: true });
  };

  return (
    <>
      <section className="panel-card cp-settings-section-header">
        <h3>Data sources</h3>
        <p className="muted">
          Manage vendor discovery sources and listing ingestion sources for this CasaMapa view.
        </p>
      </section>

      {bannerMessage ? (
        <div className="panel-card cp-settings-toast" role="status">
          {bannerMessage}
        </div>
      ) : null}

      {selectedRunId ? (
        <SourceRunLogsPanel
          clientViewId={clientViewId}
          runId={selectedRunId}
          onClose={closeRunLogs}
        />
      ) : null}

      {selectedListingRunId && selectedListingSourceKey ? (
        <ListingRunLogsPanel
          clientViewId={clientViewId}
          sourceKey={selectedListingSourceKey}
          runId={selectedListingRunId}
          onClose={closeListingRunLogs}
        />
      ) : null}

      {loading ? <ClientPortalLoading message="Loading data sources…" /> : null}
      {!loading && errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}

      {!loading && !errorMessage ? (
        <>
          <section className="panel-card cp-settings-data-sources">
            <div className="cp-settings-data-sources__section-header">
              <div>
                <h4>Vendor searches</h4>
                <p className="muted">
                  Sources that discover and refresh service providers for the vendor directory.
                </p>
              </div>
            </div>
            <div className="cvd-table-wrap">
              <table className="cvd-table">
                <thead>
                  <tr>
                    <th>Enabled</th>
                    <th>Source</th>
                    <th>Health</th>
                    <th>Last run</th>
                    <th>Last results</th>
                    <th className="cvd-table__col--actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vendorSearches.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="empty-state">
                        No vendor search sources configured.
                      </td>
                    </tr>
                  ) : (
                    vendorSearches.map((source) => (
                      <tr key={source.id}>
                        <td>
                          <SourceToggle
                            checked={Boolean(source.collectionEnabled)}
                            disabled={pendingVendorId === source.id}
                            onChange={(enabled) => handleVendorToggle(source.id, enabled)}
                            label={`Toggle ${source.name}`}
                          />
                        </td>
                        <td>
                          <SourceName
                            name={source.name}
                            code={source.sourceKey}
                            description={source.syncDescription}
                          />
                        </td>
                        <td>
                          <span className={`cvd-badge ${healthBadgeClass(source.healthStatus)}`}>
                            {formatStatusLabel(source.healthStatus)}
                          </span>
                        </td>
                        <td>
                          {source.lastRunCompletedAt
                            ? formatClientDateTime(source.lastRunCompletedAt)
                            : "—"}
                        </td>
                        <td>{formatVendorLastResults(source)}</td>
                        <td className="cvd-table__col--actions">
                          <div className="cp-settings-data-sources__actions">
                            <VendorDirectoryPermissionGate
                              permission={VENDOR_DIRECTORY_PERMISSIONS.COLLECTIONS_EXECUTE}
                            >
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                title={
                                  source.supportsRunNow
                                    ? "Run this vendor search now"
                                    : source.disabledRunNowReason ||
                                      "Enable this source to run collection."
                                }
                                disabled={
                                  source.supportsRunNow === false ||
                                  actionSourceId === source.id
                                }
                                onClick={() => handleRunNow(source.id)}
                              >
                                {actionSourceId === source.id ? "Starting…" : "Run now"}
                              </button>
                            </VendorDirectoryPermissionGate>
                            {source.lastRunId ? (
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => openRunLogs(source.lastRunId)}
                              >
                                View logs
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => setManagingSource(source)}
                            >
                              Manage
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="panel-card cp-settings-data-sources">
            <div className="cp-settings-data-sources__section-header">
              <div>
                <h4>Listing information</h4>
                <p className="muted">
                  Broker feeds that populate CasaMapa property listings. Disabling removes the source
                  from inventory and pauses ingestion.
                </p>
              </div>
            </div>
            <div className="cvd-table-wrap">
              <table className="cvd-table">
                <thead>
                  <tr>
                    <th>Enabled</th>
                    <th>Broker</th>
                    <th>Ingestion</th>
                    <th>Last run</th>
                    <th>Listings</th>
                    <th>Status</th>
                    <th className="cvd-table__col--actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {listingSources.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="empty-state">
                        No listing sources configured for this view.
                      </td>
                    </tr>
                  ) : (
                    listingSources.map((source) => (
                      <tr key={source.sourceKey}>
                        <td>
                          <SourceToggle
                            checked={Boolean(source.enabled)}
                            disabled={pendingListingKey === source.sourceKey}
                            onChange={(enabled) => handleListingToggle(source.sourceKey, enabled)}
                            label={`Toggle ${source.name}`}
                          />
                        </td>
                        <td>
                          <SourceName
                            name={source.name}
                            code={source.sourceKey}
                            description={source.syncDescription}
                          />
                        </td>
                        <td>
                          <span
                            className={`cvd-badge ${
                              source.ingestionEnabled ? "cvd-badge--published" : "cvd-badge--draft"
                            }`}
                          >
                            {source.ingestionEnabled ? "Running" : "Paused"}
                          </span>
                        </td>
                        <td>{source.lastRunAt ? formatClientDateTime(source.lastRunAt) : "—"}</td>
                        <td>{source.activeListingCount ?? 0}</td>
                        <td>
                          {source.lastErrorMessage ? (
                            <span className="cvd-badge cvd-badge--suspended">Error</span>
                          ) : (
                            <span className={`cvd-badge ${runBadgeClass(source.lastStatus)}`}>
                              {formatStatusLabel(source.lastStatus || "idle")}
                            </span>
                          )}
                        </td>
                        <td className="cvd-table__col--actions">
                          <div className="cp-settings-data-sources__actions">
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              title={
                                source.supportsRunNow
                                  ? "Run this listing source now"
                                  : source.disabledRunNowReason ||
                                    "This listing source cannot be run yet."
                              }
                              disabled={
                                !source.supportsRunNow ||
                                source.isRunning ||
                                actionListingKey === source.sourceKey
                              }
                              onClick={() => handleListingRunNow(source.sourceKey)}
                            >
                              {actionListingKey === source.sourceKey
                                ? "Starting…"
                                : source.isRunning
                                  ? "Running…"
                                  : "Run now"}
                            </button>
                            {source.lastRunId ? (
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => openListingRunLogs(source.sourceKey, source.lastRunId)}
                              >
                                View logs
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : null}

      {managingSource ? (
        <ManageVendorSearchSourceModal
          clientViewId={clientViewId}
          source={managingSource}
          onClose={() => setManagingSource(null)}
          onDeleted={(deleted) => {
            setVendorSearches((current) => current.filter((source) => source.id !== deleted.id));
            setManagingSource(null);
            showBanner(`${deleted.name} was deleted.`);
          }}
        />
      ) : null}
    </>
  );
}
